import { yupResolver } from "@hookform/resolvers/yup";
import Button from "@src/components/Button/Button";
import FormField from "@src/components/FormField/FormField";
import Input from "@src/components/Input/Input";
import Loader from "@src/components/Loader/Loader";
import { UserSettings } from "@src/types/global";
import { api } from "@src/utils/axios-instance";
import {
    useIsFetching,
    useMutation,
    useQueryClient,
} from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import * as yup from "yup";

const UserInformation = () => {
    const queries = useQueryClient();
    const data = queries.getQueryData(["userSettings"]) as UserSettings;

    const schema = yup.object().shape({
        username: yup
            .string()
            .min(1)
            .max(255)
            .required("Please provide a valid username"),
        email: yup
            .string()
            .when([], (_email, schema) => {
                if (dirtyFields.password) {
                    return schema.required().email(
                        "Please enter a valid e-mail.",
                    );
                } else {
                    return schema;
                }
            }),
        password: yup
            .string()
            .nullable()
            .min(12, "Password must be at least 12 characters")
            .matches(
                /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$ %^&*-]).{12,}$/,
                {
                    message:
                        "Password must be 12 characters long, at least one upper case letter, one lower case letter, one number and one special character",
                },
            )
            .transform((_, value) => (value === "" ? null : value)),
        current_password: yup
            .string()
            .nullable()
            .when("password", (_password, schema) => {
                if (dirtyFields.password) {
                    return schema.required(
                        "Enter your password to confirm changes.",
                    );
                } else {
                    return schema;
                }
            })
            .when("email", (_email, schema) => {
                if (dirtyFields.email) {
                    return schema.required(
                        "Enter your password to confirm changes.",
                    );
                } else {
                    return schema;
                }
            }),
    });

    interface FormValues extends yup.InferType<typeof schema> { }
    async function updateUser({
        action,
        data,
    }: {
        action: string;
        data: Partial<FormValues>;
    }) {
        const res = await api.patch("/users", {
            _action: action,
            params: data,
        });
        console.log(res);
        return res.data.data;
    }
    const mutation = useMutation({
        mutationFn: updateUser,
        mutationKey: ["userSettings"],
        onSuccess: (data) => {
            toast(data);
        },
    });

    const {
        handleSubmit,
        register,
        formState: { errors, dirtyFields },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            username: data.username,
            email: undefined,
            current_password: undefined,
            password: "",
        },
    });
    function submit(formData: FormValues) {
        const { current_password, ...rest } = dirtyFields;
        if (Object.keys(rest).length == 0) {
            toast("No changes were made");
            return;
        }
        if (dirtyFields.email != null) {
            mutation.mutate({
                action: "change_email",
                data: {
                    email: formData.email,
                    current_password: formData.current_password,
                },
            });
        }
        if (dirtyFields.username)
            mutation.mutate({
                action: "all",
                data: {
                    username: formData.username,
                    current_password: formData.current_password,
                },
            });
        if (dirtyFields.password) {
            mutation.mutate({
                action: "change_password",
                data: {
                    password: formData.password,
                    current_password: formData.current_password,
                },
            });
        }
    }
    return (
        <>
            {useIsFetching({ queryKey: ["userSettings"] }) ? (
                <div style={{ height: "339px", marginBottom: "1rem" }}>
                    <Loader />
                </div>
            ) : (
                <form onSubmit={handleSubmit(submit)}>
                    <FormField name="username" errors={errors}>
                        <Input name="username" required register={register} />
                    </FormField>
                    <FormField name="email" errors={errors}>
                        <Input name="email" placeholder={data?.email || ""} register={register} />
                    </FormField>

                    <FormField name="password" errors={errors}>
                        <Input
                            type="password"
                            name="password"
                            placeholder="Your password"
                            register={register}
                        />
                    </FormField>

                    <FormField
                        name="current_password"
                        errors={errors}
                        hideLabel={true}
                    >
                        <label
                            htmlFor="current_password"
                            className="input__label"
                        >
                            Enter your current password to validate changes
                        </label>
                        <Input
                            type="password"
                            name="current_password"
                            placeholder="Current password"
                            register={register}
                        />
                    </FormField>
                    <Button type="submit" preset="primary">
                        Update
                    </Button>
                </form>
            )}
        </>
    );
};
export default UserInformation;
